import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { map, Observable, take } from 'rxjs';
import {
  ActiveCarryOverLoads,
  CloseActiveCarryOverLoadsRequest,
} from '../../models/position-operations/carry-over-loads.model';
import {
  FlatLoadKey,
  GleLoadsRequest,
  GleLoadsResponse,
  UpdateGleLoad,
  UpdateGleLoadRequest,
  UpdateGleLoadResponse,
} from '../../models/position-operations/outbound-scan-gle.model';
import { ActiveCarryOverLoadsService } from '../../services/position-operations/active-carry-over-loads/active-carry-over-loads.service';

@Injectable({
  providedIn: 'root',
})
export class CarryOverLoadsUtils {
  constructor(
    readonly _activeCarryOverLoadsService: ActiveCarryOverLoadsService,
  ) {}

  /**
   * Closes the load with the provided details.
   *
   * @param tableData - Array of ActiveCarryOverLoads.
   * @param closeDate - The date when the load is closed.
   * @param closeTime - The time when the load is closed.
   * @param form - The form group containing additional data.
   * @param index - The index of the load in the tableData array.
   */
  closeTheLoad(
    tableData: ActiveCarryOverLoads[],
    closeDate: Date,
    closeTime: string,
    form: FormGroup,
    index: number,
    smartBagCheck?: boolean,
    id?: number,
    autoLinkAndClose?: boolean,
  ) {
    const load = tableData[index];

    const request: CloseActiveCarryOverLoadsRequest = {
      uldNumber: load.uldNumber,
      uldCount: load.uldCount,
      pkgCount: load.pkgCount,
      origSort: load.origSort,
      loadKey: load.loadKey,
      loadCreateDate: load.loadCreateDate,
      childLoads: load.childLoads,
      uldType: load.uldType,
      sealNumber: load.sealNumber,
      loadChart: load.loadChart,
      movement: load.movement,
      pctFull: form.get('percentFull')?.value,
      closeDate: this.formatDate(closeDate),
      time: this.formatTime(closeTime),
      smartBagCheck: smartBagCheck ?? false,
      autoLinkAndClose: autoLinkAndClose ?? false,
      id: id ?? load.id,
    };

    return this._activeCarryOverLoadsService.closeActiveLoad(request);
  }

  getGleLoad(
    data: GleLoadsRequest,
    selectedLoad: ActiveCarryOverLoads,
  ): Observable<GleLoadsResponse> {
    const request: GleLoadsRequest = {
      uldNumber: selectedLoad.uldNumber,
      uldType: selectedLoad.uldType,
      loadKey: selectedLoad.loadKey,
      loadCreateDate: selectedLoad.loadCreateDate,
      originSort: selectedLoad.origSort,
      childLoads: [],
      sortDate: this.formatDate(data.sortDate),
      sortCode: data.sortCode,
      bay: data.bay,
      belt: data.belt,
    };

    return this._activeCarryOverLoadsService.getGleLoads(request).pipe(
      take(1),
      map((res: GleLoadsResponse) => res),
    );
  }

  updateGleLoad(
    data: UpdateGleLoad,
    selectedLoad: ActiveCarryOverLoads,
    latestSelectedLoad?: FlatLoadKey,
  ): Observable<UpdateGleLoadResponse> {
    const firstLoadKey =
      data?.loadKeys?.find(
        (data) =>
          data.selectedLoadName === latestSelectedLoad?.selectedLoadName,
      ) ?? data?.loadKeys?.[0];

    const request: UpdateGleLoadRequest = {
      sortDate: data?.sortDate,
      sortCode: data?.sortCode,
      belt: data?.belt,
      bay: data?.bay,
      uloId: data?.uloId ?? 1556851601,
      equipmentNumber: data?.equipmentNumber ?? selectedLoad.uldNumber,
      equipmentType: data?.equipmentType ?? selectedLoad.uldType,
      selectedLoad:
        firstLoadKey?.selectedLoadName ??
        selectedLoad?.loadKey ??
        selectedLoad?.uldNumber,
      loadType: 'LD',
      origCountry: firstLoadKey?.origCountry,
      origSlic: firstLoadKey?.origSlic,
      origPortcode: firstLoadKey?.origPortcode,
      origSortcode: firstLoadKey?.origSortcode,
      destCountry: firstLoadKey?.destCountry,
      destSlic: firstLoadKey?.destSlic,
      destPortcode: firstLoadKey?.destPortcode,
      destSortcode: firstLoadKey?.destSortcode,
      srvLevel: firstLoadKey?.srvLevel,
      procCountry: firstLoadKey?.procCountry,
      procSlic: firstLoadKey?.procSlic,
      procSortcode: firstLoadKey?.procSortcode,
      preference: firstLoadKey?.preference,
      scanType: firstLoadKey?.scanType,
      flowType: firstLoadKey?.flowType,
      ...(data?.sealNumber && {
        sealNumber: data?.sealNumber ?? selectedLoad.sealNumber,
        mvmtNum: data?.movement ?? selectedLoad.movement,
        mvmtType: firstLoadKey?.mvmtType,
      }),
      origSortQual: firstLoadKey?.origSortQual,
      destSortQual: firstLoadKey?.destSortQual,
      procSortQual: firstLoadKey?.procSortQual,
      loadChart: firstLoadKey?.loadChart,
      procPortcode: firstLoadKey?.procPortcode,
    };

    return this._activeCarryOverLoadsService.updateGleLoad(request);
  }

  formatTime(value: string): string {
    const formattedTime = value?.replace(':', '');
    return formattedTime;
  }

  formatDate(value: string | Date): string {
    return formatDate(value, 'yyyy-MM-dd', 'en-US');
  }
}
